export const MAPAPET_THEME_STORAGE_KEY = "mapapet-theme";
export const MAPAPET_ADMIN_THEME_STORAGE_KEY = "mapapet-admin-theme";

const MAPAPET_THEME_COOKIE = "mapapetTheme";
const MAPAPET_ADMIN_THEME_COOKIE = "mapapetAdminTheme";
const DARK_THEME = "dark";
const LIGHT_THEME = "light";
const SITE_SCOPE = "site";
const ADMIN_SCOPE = "admin";

const isBrowser = () =>
  typeof window !== "undefined" &&
  typeof document !== "undefined";

const normalizeTheme = (value) =>
  value === DARK_THEME || value === LIGHT_THEME
    ? value
    : null;

const normalizeScope = (scope) =>
  scope === ADMIN_SCOPE ? ADMIN_SCOPE : SITE_SCOPE;

const getStorageKey = (scope) =>
  normalizeScope(scope) === ADMIN_SCOPE
    ? MAPAPET_ADMIN_THEME_STORAGE_KEY
    : MAPAPET_THEME_STORAGE_KEY;

const getCookieName = (scope) =>
  normalizeScope(scope) === ADMIN_SCOPE
    ? MAPAPET_ADMIN_THEME_COOKIE
    : MAPAPET_THEME_COOKIE;

export const getThemeScopeForPath = (pathname = "") =>
  String(pathname || "").startsWith("/admin")
    ? ADMIN_SCOPE
    : SITE_SCOPE;

const readThemeCookie = (scope = SITE_SCOPE) => {
  if (!isBrowser()) return null;

  const cookieName = getCookieName(scope);
  const match = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) =>
      part.startsWith(`${cookieName}=`)
    );

  if (!match) return null;

  return normalizeTheme(
    decodeURIComponent(match.split("=").slice(1).join("="))
  );
};

export const getStoredTheme = (scope = SITE_SCOPE) => {
  if (!isBrowser()) return null;

  try {
    const localTheme = normalizeTheme(
      window.localStorage.getItem(
        getStorageKey(scope)
      )
    );

    if (localTheme) return localTheme;
  } catch (error) {
    console.warn(
      "[Mapapet] Не удалось прочитать тему из localStorage:",
      error
    );
  }

  return readThemeCookie(scope);
};

export const getInitialTheme = (scope = SITE_SCOPE) => {
  const normalizedScope = normalizeScope(scope);
  const storedTheme = getStoredTheme(normalizedScope);

  if (storedTheme) return storedTheme;

  /*
   * Mapapet по умолчанию всегда открывается в светлой теме.
   * Системная тема браузера/ОС намеренно не влияет на сайт.
   * Тёмная тема включается только явным выбором пользователя.
   */
  return LIGHT_THEME;
};

export const applyTheme = (theme) => {
  if (!isBrowser()) return LIGHT_THEME;

  const normalizedTheme =
    normalizeTheme(theme) || LIGHT_THEME;

  document.documentElement.setAttribute(
    "data-theme",
    normalizedTheme
  );

  if (document.body) {
    document.body.setAttribute(
      "data-theme",
      normalizedTheme
    );
  }

  document.documentElement.style.colorScheme =
    normalizedTheme;

  const themeColor =
    normalizedTheme === DARK_THEME
      ? "#0f1720"
      : "#f9f9f9";

  let metaThemeColor = document.querySelector(
    'meta[name="theme-color"]'
  );

  if (!metaThemeColor) {
    metaThemeColor = document.createElement("meta");
    metaThemeColor.setAttribute("name", "theme-color");
    document.head.appendChild(metaThemeColor);
  }

  metaThemeColor.setAttribute("content", themeColor);

  return normalizedTheme;
};

export const saveThemePreference = (
  theme,
  scope = SITE_SCOPE
) => {
  if (!isBrowser()) return;

  const normalizedTheme =
    normalizeTheme(theme) || LIGHT_THEME;
  const normalizedScope = normalizeScope(scope);
  const cookieName = getCookieName(normalizedScope);

  try {
    window.localStorage.setItem(
      getStorageKey(normalizedScope),
      normalizedTheme
    );
  } catch (error) {
    console.warn(
      "[Mapapet] Не удалось сохранить тему в localStorage:",
      error
    );
  }

  const secure =
    window.location.protocol === "https:"
      ? "; Secure"
      : "";

  const sharedDomain =
    /(^|\.)mapapet\.com$/i.test(window.location.hostname)
      ? "; Domain=.mapapet.com"
      : "";

  document.cookie =
    `${cookieName}=${encodeURIComponent(normalizedTheme)}` +
    `; Max-Age=31536000; Path=/; SameSite=Lax${sharedDomain}${secure}`;
};
